import React, { useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import Zone from '@wix/da-gruser-shared/pkg/components/Zone';
import ZoneGroup from '@wix/da-gruser-shared/pkg/components/ZoneGroup';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import LazyContext from '@wix/da-shared-react/pkg/LazyLoadingImage/LazyContext';
import { getExhibitionistStripWidgetConfig } from '@wix/da-shared-react/pkg/widgets/Exhibitionist/widgetConfig';
import { getModuleByModuleType } from '@wix/da-gruser-shared/pkg/redux/selectors/modules';
import useGruser from '@wix/da-gruser-shared/pkg/hooks/useGruser';
import { getProfileOwnerUser } from '../../../selectors/users';
import { getHomeWidgetConfigs } from './homeWidgetConfigs';

import s from './HomeSection.scss';

export interface Props {}

const HomeSection: React.FC<Props> = props => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { gruser } = useGruser();
  const profileOwnerUser = useSelector(getProfileOwnerUser);
  const isMobile = useContext(MobileContext);

  const homeWidgetConfigs = getHomeWidgetConfigs({ t, dispatch });
  const topBarWidgetConfigs = {
    ...homeWidgetConfigs,
    exhibitionist: getExhibitionistStripWidgetConfig({ t, dispatch }),
  };

  const hasAboutMeInstalled = Boolean(
    useSelector<any, any>(state =>
      getModuleByModuleType(state, gruser, 'about')
    )
  );

  const renderOneColumn = () => (
    <ZoneGroup
      disableTopAddWidget={true}
      widgetConfigs={homeWidgetConfigs}
      className={s['zone-group']}
    >
      <Zone name="topRight" widgetConfigs={homeWidgetConfigs} />
      <Zone name="megaBar" direction="row" widgetConfigs={homeWidgetConfigs} />
      <Zone name="topBar" widgetConfigs={homeWidgetConfigs} />
      {
        // We originally launched Eclipse with Exhibionist module being
        // pre-installed in the topLeft zone, but we've later disabled option to
        // install it there. If we do not include this zone all the accounts with
        // pre-installed exhibitionist modules will lose them.
      }
      <Zone name="topLeft" widgetConfigs={homeWidgetConfigs} />
      <Zone name="midRight" widgetConfigs={homeWidgetConfigs} />
      <Zone name="bottomLeft" widgetConfigs={homeWidgetConfigs} />
      <Zone name="bottomRight" widgetConfigs={homeWidgetConfigs} />
    </ZoneGroup>
  );

  const renderTwoColumns = () => (
    <>
      <ZoneGroup className={s['zone-top']} widgetConfigs={topBarWidgetConfigs}>
        <Zone
          name="megaBar"
          direction="row"
          widgetConfigs={topBarWidgetConfigs}
        />
        <Zone name="topBar" widgetConfigs={topBarWidgetConfigs} />
      </ZoneGroup>
      <div className={s['flex-wrap']}>
        <ZoneGroup
          className={s['zone-column-left']}
          widgetConfigs={homeWidgetConfigs}
          disableTopAddWidget={hasAboutMeInstalled}
        >
          <Zone name="topLeft" widgetConfigs={homeWidgetConfigs} />
          <Zone name="bottomLeft" widgetConfigs={homeWidgetConfigs} />
        </ZoneGroup>
        <ZoneGroup
          className={s['zone-column-right']}
          widgetConfigs={homeWidgetConfigs}
        >
          <Zone name="topRight" widgetConfigs={homeWidgetConfigs} />
          <Zone name="midRight" widgetConfigs={homeWidgetConfigs} />
          <Zone name="bottomRight" widgetConfigs={homeWidgetConfigs} />
        </ZoneGroup>
      </div>
    </>
  );

  return (
    <div className={s['root']}>
      <BiLoggerContextProvider
        value={{ creatorid: profileOwnerUser.useridUuid }}
      >
        <LazyContext.Provider value={true}>
          {isMobile ? renderOneColumn() : renderTwoColumns()}
        </LazyContext.Provider>
      </BiLoggerContextProvider>
    </div>
  );
};
HomeSection.displayName = 'HomeSection';

export default HomeSection;
